import {
  trackCheckoutErrorShown,
  trackCheckoutFailure,
  type CheckoutFailureProperties,
} from '../services/analytics';
import { confirmDialog, showDialog } from './dialogs';

type Translate = (key: string, vars?: Record<string, any>) => string;

type CheckoutErrorDetails = {
  errorCode: string;
  errorType: string;
};

export type CheckoutFailureFeedback = CheckoutErrorDetails & {
  escalated: boolean;
  failureCount: number;
  message: string;
};

type ReportCheckoutFailureOptions = {
  error: unknown;
  surface: string;
  checkoutKey?: string;
  properties?: Record<string, any>;
  t: Translate;
};

type ShowCheckoutFailureDialogOptions = ReportCheckoutFailureOptions & {
  feedback?: CheckoutFailureFeedback;
  retry?: () => unknown | Promise<unknown>;
};

const sessionFailureCounts = new Map<string, number>();

const sanitizeDiagnostic = (value: unknown): string | null => {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const sanitized = String(value)
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64);
  return sanitized || null;
};

export const getCheckoutErrorDetails = (error: unknown): CheckoutErrorDetails => {
  const candidate = error as any;
  const status = candidate?.response?.status;
  const conflictCode = candidate?.billingCheckoutConflict?.code;
  const responseCode =
    candidate?.response?.data?.error_code ??
    candidate?.response?.data?.code;
  const localCode = candidate?.code;

  const errorCode =
    sanitizeDiagnostic(conflictCode) ||
    sanitizeDiagnostic(responseCode) ||
    sanitizeDiagnostic(localCode) ||
    (typeof status === 'number' ? `http_${status}` : 'unknown_checkout_error');

  let errorType = 'exception';
  if (typeof status === 'number') {
    errorType = `http_${status}`;
  } else if (
    ['network_error', 'err_network', 'econnaborted', 'etimedout'].includes(errorCode) ||
    candidate?.name === 'NetworkError'
  ) {
    errorType = 'network';
  } else if (errorCode === 'missing_checkout_url' || errorCode === 'missing_client_secret') {
    errorType = 'invalid_response';
  } else if (errorCode === 'stripe_js_load') {
    errorType = 'stripe_load';
  } else if (errorCode === 'popup_blocked') {
    errorType = 'browser';
  }

  return { errorCode, errorType };
};

export const createCheckoutError = (code: string): Error => {
  const error = new Error(code);
  (error as Error & { code: string }).code = code;
  return error;
};

export const reportCheckoutFailure = ({
  error,
  surface,
  checkoutKey = 'default',
  properties,
  t,
}: ReportCheckoutFailureOptions): CheckoutFailureFeedback => {
  const countKey = `${surface}:${checkoutKey}`;
  const failureCount = (sessionFailureCounts.get(countKey) || 0) + 1;
  sessionFailureCounts.set(countKey, failureCount);

  const { errorCode, errorType } = getCheckoutErrorDetails(error);
  const escalated = failureCount >= 3;
  const analyticsProperties: CheckoutFailureProperties = {
    ...properties,
    error_code: errorCode,
    error_type: errorType,
    surface,
    failure_count: failureCount,
    escalated,
  };
  trackCheckoutFailure(analyticsProperties);

  return {
    errorCode,
    errorType,
    escalated,
    failureCount,
    message: t(
      escalated
        ? 'billing.checkout.failureEscalated'
        : 'billing.checkout.failureMessage',
    ),
  };
};

export const markCheckoutErrorShown = (
  feedback: CheckoutFailureFeedback,
  surface: string,
  properties?: Record<string, any>,
) => {
  trackCheckoutErrorShown({
    ...properties,
    error_code: feedback.errorCode,
    error_type: feedback.errorType,
    surface,
    failure_count: feedback.failureCount,
    escalated: feedback.escalated,
  });
};

export const showCheckoutFailureDialog = async ({
  feedback: existingFeedback,
  retry,
  ...options
}: ShowCheckoutFailureDialogOptions): Promise<void> => {
  const feedback = existingFeedback || reportCheckoutFailure(options);
  markCheckoutErrorShown(feedback, options.surface, options.properties);

  if (!retry) {
    showDialog(
      options.t('billing.checkout.failureTitle'),
      feedback.message,
      [{ text: options.t('common.ok') }],
    );
    return;
  }

  const shouldRetry = await confirmDialog(
    options.t('billing.checkout.failureTitle'),
    feedback.message,
    options.t('billing.checkout.retry'),
    options.t('common.cancel'),
  );
  if (shouldRetry) {
    try {
      await retry();
    } catch (retryError) {
      await showCheckoutFailureDialog({
        ...options,
        error: retryError,
        retry,
      });
    }
  }
};
